"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, ShieldCheck, Server, CircleCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function UninterruptedOperationHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
    }> = [];
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(Math.floor(canvas.width * canvas.height / 15000), 100);
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5
        });
      }
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "rgba(6, 182, 212, 0.5)";
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fill();
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.15 * (1 - distance / 150)})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });
      animationFrameId = requestAnimationFrame(draw);
    };
    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    draw();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section data-section-id="42121" id="ψηφιακό-φυσικό-οχυρό-για-την-επιχείρησή-σας" className="dark bg-[#020617] text-slate-50 relative min-h-[90vh] flex items-center overflow-hidden py-20">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 z-0 w-full h-full opacity-30 pointer-events-none" aria-hidden="true" />

      {}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#020617] via-[#020617]/80 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={contentRef} className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="space-y-8 max-w-2xl">
            <div className="space-y-2">
              <h1 className="text-4xl md:text-7xl font-bold tracking-tighter leading-tight uppercase">
                <span className="text-slate-50 block drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]">ΑδιΑλειπτη Λειτουργια</span>
                <span className="text-cyan-400 block mt-2 drop-shadow-[0_0_20px_rgba(6,182,212,0.4)]">για την ΕπιχεΙρησΗ σας</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-400 max-w-lg pt-4 font-sans leading-relaxed">
                Η <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services" className="h-7 inline-block align-middle" /> θωρακίζει την επιχείρησή σας με ολοκληρωμένες λύσεις δικτύων, servers, access control και συστημάτων ασφαλείας τελευταίας τεχνολογίας.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="bg-gradient-to-r from-cyan-500 to-indigo-600 text-white hover:opacity-90 shadow-[0_0_25px_rgba(6,182,212,0.3)] text-lg h-14 px-8 rounded-full transition-all hover:scale-105 border-none">
                <Link to="/ServicesPage">
                  <ShieldCheck className="mr-2 h-5 w-5" />
                  Εξερευνήστε Λύσεις
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="border-white/10 hover:bg-white/5 text-slate-50 h-14 px-8 rounded-full backdrop-blur-md">
                <Link to="/ContactPage">
                  Επικοινωνία
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-400 pt-4 font-mono uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-cyan-500" />
                <span>24/7 ΥποστΗριξη</span>
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-cyan-500" />
                <span>ΑπΟλυτη ΑσφΑλεια</span>
              </div>
            </div>
          </div>

          {}
          <div className="relative hidden lg:block perspective-1000">
            <div className="absolute -inset-4 bg-cyan-500/10 rounded-full blur-3xl animate-pulse opacity-40" />
            
            <Card className="relative bg-[#0F172A]/50 backdrop-blur-2xl border-white/10 shadow-2xl transform transition-transform hover:scale-[1.02] duration-500 overflow-hidden border-t-white/10 border-l-white/10 rounded-2xl">
              <CardHeader className="border-b border-white/5 pb-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400">
                      <Server className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-bold text-slate-50 uppercase tracking-tight">ΚΕντρο ΕπιχειρΗσεων</CardTitle>
                      <p className="text-xs text-slate-500 font-mono">Real-time System Monitoring</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 px-3 py-1 animate-pulse font-mono">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 inline-block" />
                    ACTIVE
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pt-6 space-y-6">
                {}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm font-mono uppercase tracking-tighter">
                    <span className="text-slate-400">Uptime ΔικτΥου</span>
                    <span className="text-cyan-400 font-bold">99.99%</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 w-[99.9%] shadow-[0_0_15px_#06b6d4]" />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm font-mono uppercase tracking-tighter">
                    <span className="text-slate-400">ΑπειλΕς που ΑποκλεΙστηκαν</span>
                    <span className="text-indigo-400 font-bold">1,842</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 w-[85%] shadow-[0_0_15px_#6366f1]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1 text-slate-500 text-[10px] font-mono uppercase">
                      <Activity className="h-3 w-3" />
                      Latency
                    </div>
                    <div className="text-xl font-bold text-slate-50 font-mono">12ms</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1 text-slate-500 text-[10px] font-mono uppercase">
                      <ShieldCheck className="h-3 w-3" />
                      Firewall
                    </div>
                    <div className="text-xl font-bold text-cyan-400 font-mono">ON</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}